import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Col, Container, Row } from 'react-bootstrap';
import CircledIcon from '../../../components/CircledIcon';
import useMobile from '../../../hooks/useMobileHook';
import GenericTable, { tableDataProps } from '../../../components/GenericTable';
import { formatPrice } from '../../../utils/utils';
import CalloutCard from '../../../components/CalloutCard';
import IconButton from '../../../components/IconButton';
import { useState } from 'react';
import DefaultCard from '../../../components/DefaultCard';
import Button from '../../../components/Button';
import { 
  getHealthBenefitsDescriptionTitles,
  getHealthBenefitsDescription, 
} from './info/HealthBenefitsInfo'
import Link from '../../../components/Link';

export type customHealthBenefitsTabProps = {
  healthBenefits: any;
  headerTitle?: String;
};

const HealthBenefitsTab = ({
  healthBenefits,
  headerTitle,
}: customHealthBenefitsTabProps) => {

  const isMobile = useMobile();
  const [showDetails, setShowDetails] = useState<any>({
    medical: false,
    healthsavings: false,
    criticalillness: false,
    accidentinsurance: false,
    hospitalindemnity: false,
    dental: false,
    vision: false,
    spousalsurcharge: false,
    nontobaccousercredit: false,
  });

  const toggleDetails = (type: String) => {
    let key = type.replace('-', '');
    let value = showDetails[`${key}`];
    setShowDetails({...showDetails, [key]: !value});
  };

  const [tellMeMore, setTellMeMore] = useState<any>({
    medical: false,
    healthsavings: false,
    criticalillness: false,
    accidentinsurance: false,
    hospitalindemnity: false,
    dental: false,
    vision: false,
    spousalsurcharge: false,
    nontobaccousercredit: false,
  });

  const handleTellMeMore = (type: String) => {
    let key = type.replace('-', '');
    let value = tellMeMore[`${key}`];
    setTellMeMore({...tellMeMore, [key]: !value});
  };

  const getTableData = (plans: any) => {
    let tableData: tableDataProps = {
      headers: ['Plan', 'You', 'You & Spouse', 'You & Children', 'You & Family'],
      rows: []
    };

    if (!Array.isArray(plans)) {
      return {
        headers: ['Options', 'Annual Contribution Amount', 'Per Pay Period Contribution'],
        rows: [
          [
            'Contribution',
            formatPrice(plans['cost']*12),
            formatPrice(plans['cost']),
          ],
          [
            'No Coverage',
            formatPrice(0),
            formatPrice(0),
          ]
        ]
      };
    }

    plans.map( (plan, i) => {
      tableData = plan.familyMembersPrices ? {
        ...tableData,
        rows: [...tableData['rows'],
          [
            plan.title,
            formatPrice(plan.familyMembersPrices?.you),
            formatPrice(plan.familyMembersPrices?.youAndSpouse),
            formatPrice(plan.familyMembersPrices?.youAndChildern),
            formatPrice(plan.familyMembersPrices?.youAndFamily),
          ],
        ],
      } : {
        ...tableData,
        headers: ['Option', 'Per Pay Period Cost'],
        rows: [...tableData['rows'],
          [
            plan.title,
            plan.costType === 'credit' ? `credit (${formatPrice(Math.abs(plan.cost))})` : formatPrice(plan.cost),
          ],
        ]
      };
      return plan;
    });
    return tableData;
  }

  const getCellCoordinates = (benefit: any) => {
    let y = benefit['profileIds'].length > 4 ? 4 : benefit['profileIds'].length;
    if (!y) y = 1;
    let x = 1;
    benefit['availablePlans'].map(
      (b: any, i: number) => {
        if (b.id === benefit['plan'].id) x = i;
        return b;
      }
    );    
    return [x,y];
  }



  const getSelectedRow = (benefit: any) => {
    if (benefit['type'] !== 'health-savings') return undefined;
    return benefit['plan'].id === 'hsa-noCoverage' ? 1 : 0;
  }



  const getMinimumContribution = (string: String) => {
    return string.replace('Range ', '').split(' - ')[0];
  }

  const getMaximumContribution = (string: String) => {
    return string.replace('Range ', '').split(' - ')[1];
  }

  const icon = (iconName: IconProp) => (
    <CircledIcon
      variant={'icon-emphasized'}
      size={isMobile ? 'icon-sm' : 'icon-lg'}
      icon={iconName}
    />
  );

  const getCardIcon = (type: String) => {
    switch (type) {
      case 'heart-pulse':
        return light('heart-pulse');
      case 'tooth':
        return light('tooth');
      case 'glasses':
        return light('glasses');
      case 'face-thermometer':
        return light('face-thermometer');
      case 'family':
        return light('family');
      case 'bed-pulse':
        return light('bed-pulse');
      case 'hospitals':
        return light('hospitals');
      case 'gavel':
        return light('gavel');
      case 'clipboard-medical':
        return light('clipboard-medical');
      default:
        return light('image-slash');
    }
  };

  return (
    <Container className={'px-0'}>
      <Row className={`align-items-center ${headerTitle ? 'mt-8' : ''}`}>
        <Col className={'col-12 col-lg-6'}>
          {headerTitle && <span className={'text-sm-16-lg-24 fw-normal lx-xxs'}>
            {headerTitle}
          </span>}
        </Col>
        <div>
          {healthBenefits.map((benefit: any, index: number) => {
            return (
              <Container className={`m-0 ${index || headerTitle ? 'mt-10' : ''} p-10 border border-info rounded-1`}>
                <div className={'d-flex justify-content-between align-items-center mb-10'}>
                  <Col>
                    {icon(getCardIcon(benefit['generic'].icon))}
                    <span className={'h5 mb-0 ms-4'}>{benefit['generic'].title}</span>
                  </Col>
                  {benefit['type'] !== 'spousal-surcharge' 
                    && benefit['type'] !== 'accident-insurance'
                    && benefit['type'] !== 'non-tobacco-user-credit'
                    && <div onClick={() => toggleDetails(benefit['type'])} className={'icon-button-wrapper'}>
                    <IconButton
                      variant="icon-default"
                      icon={light('circle-exclamation')}
                      extLabel="Benefit Info"
                    />
                  </div>}
                </div>
                <Row className={`expandable-wrapper ${showDetails[benefit['type'].replace('-', '')] ? 'open' : ''}`}>
                  <div className="d-flex justify-content-center">
                    <DefaultCard
                      closeButton={
                        <Link 
                          className='pt-0' 
                          isAlignAtEnd 
                          isWhiteIcon 
                          rightIcon={light('close')}
                          onClick={() => toggleDetails(benefit['type'])}
                        />                      
                      }
                      variant="blue-motif"
                      leftHeading={
                        getHealthBenefitsDescriptionTitles(benefit['type'])
                      }
                      leftSubheading={
                        <Container className={'px-0 d-flex flex-column'}>
                          <span>
                            {getHealthBenefitsDescription(benefit['type'], tellMeMore[`${benefit['type'].replace('-', '')}`])}
                          </span>
                        </Container>
                      }
                      button={
                        <>
                          {benefit['type'] !== 'vision' && <Button
                            onClick={() => handleTellMeMore(benefit['type'])}
                            variant="primary-shadow"
                            size="sm"
                          >
                            {tellMeMore[`${benefit['type'].replace('-', '')}`] ? 'Tell me less' : 'Tell me more'}
                          </Button>}
                        </>
                      }
                      className={'mb-12'}
                    />
                  </div>
                </Row>
                {benefit['type'] === 'health-savings' && <CalloutCard>
                  <Row className='col-12 col-lg-5 justify-content-between'>
                    <Col className='p-0 col-8 col-lg-12 text-start'>
                      <b>Minimum Contribution</b>
                    </Col>
                    <div className='p-0 col-4 col-lg-12 text-end text-lg-start'>
                      {getMinimumContribution(benefit['plan']?.subtitle2)}
                    </div>
                  </Row>
                  <Row className='col-12 col-lg-3 justify-content-between'>
                    <Col className='p-0 col-8 col-lg-12 text-start'>
                      <b>Maximum Contribution</b>
                    </Col>
                    <div className='p-0 col-4 col-lg-12 text-end text-lg-start'>
                      {getMaximumContribution(benefit['plan']?.subtitle2)}
                    </div>
                  </Row>
                  <Row className='col-12 col-lg-4 justify-content-between pe-lg-10'>
                    <Col className='p-0 col-4 col-lg-12 text-start text-lg-end'>
                      <b>Increments of</b>
                    </Col>
                    <div className='p-0 col-4 col-lg-12 text-end'>{formatPrice(1)}</div>
                  </Row>
                </CalloutCard>}
                <Col className={'mt-10 overflow-auto'}>
                  <GenericTable
                    className={benefit['generic'].familyMembers 
                      ? 'custom-row-2' : benefit['type'] === 'health-savings' 
                      ? '' : 'custom-row-1'}
                    headerTitle={(benefit['generic'].familyMembers && 'Per-pay-period cost')}
                    selectedRowIndex={getSelectedRow(benefit)}
                    selectedCellPosition={benefit['type'] === 'health-savings' ? undefined : getCellCoordinates(benefit)}
                    tableData={getTableData(benefit['type'] === 'health-savings' ? benefit['plan'] : benefit['availablePlans'])}
                    downloadButton={benefit['type'] === 'accident-insurance'
                      || benefit['type'] === 'medical' 
                      || benefit['type'] === 'dental'
                      || benefit['type'] === 'vision'
                      ? 'icon-default' : undefined}
                  />
                </Col>
              </Container>
            );
          })}
        </div>
      </Row>
    </Container>
  );
};

export default HealthBenefitsTab;
